import { Block, Line } from '../types';
import { STYLES } from '../styles/constants';

/** Taille de référence (px) pour le calcul « plus de police = moins de caractères visibles ». */
export const DISPLAY_BASE_FONT = 14;

/** Référence à 14px : tâche / start / end. */
export const PROCESS_DISPLAY_BASE_CHARS = 40;
/** Références à 14px : losange décision (texte total sur 2 lignes). */
export const DECISION_DISPLAY_BASE_CHARS = 25;

/**
 * Nombre de caractères affichables selon la taille de police (inverse : 12px → plus long, 16px → plus court).
 * @param baseCharsAt14 — nombre de caractères à 14px pour ce type de bloc
 */
export function getVisibleCharCap(fontSize: number | undefined, baseCharsAt14: number): number {
  const fs = fontSize && fontSize > 0 ? fontSize : DISPLAY_BASE_FONT;
  const minChars = baseCharsAt14 <= 28 ? 8 : 12;
  return Math.max(minChars, Math.round(baseCharsAt14 * (DISPLAY_BASE_FONT / fs)));
}

type BlockDimensionInput = Pick<Block, 'type' | 'width' | 'height'>;

function getDefaultBlockDimensionsForType(type: Block['type']): { width: number; height: number } {
  switch (type) {
    case 'start':
    case 'end':
      return {
        width: STYLES.startEnd.width,
        height: STYLES.startEnd.height,
      };
    case 'decision':
      return {
        width: STYLES.decision.width,
        height: STYLES.decision.height,
      };
    case 'process':
    default:
      return {
        width: STYLES.process.width,
        height: STYLES.process.height,
      };
  }
}

/**
 * Ajuste la capacité de caractères selon les dimensions du bloc.
 * Aux dimensions par défaut, retourne exactement baseCap (comportement inchangé).
 */
function scaleCharCapByBlockDimensions(
  baseCap: number,
  block: BlockDimensionInput,
  minScaledChars: number
): number {
  const dims = getBlockDimensions(block as Block);
  const defaults = getDefaultBlockDimensionsForType(block.type);

  if (dims.width === defaults.width && dims.height === defaults.height) {
    return baseCap;
  }

  const widthScale = dims.width / defaults.width;
  const heightScale = dims.height / defaults.height;
  return Math.max(minScaledChars, Math.round(baseCap * widthScale * heightScale));
}

/** Tâche : base 40 à 14px. */
export function getProcessLikeVisibleCharCap(
  fontSize?: number,
  block?: BlockDimensionInput
): number {
  const baseCap = getVisibleCharCap(fontSize, PROCESS_DISPLAY_BASE_CHARS);
  if (!block || block.type !== 'process') {
    return baseCap;
  }
  return scaleCharCapByBlockDimensions(baseCap, block, 8);
}

/** Start / end : base 40 à 14px, mise à l’échelle selon les dimensions du bloc. */
export function getStartEndVisibleCharCap(
  fontSize?: number,
  block?: BlockDimensionInput
): number {
  const baseCap = getVisibleCharCap(fontSize, PROCESS_DISPLAY_BASE_CHARS);
  if (!block || (block.type !== 'start' && block.type !== 'end')) {
    return baseCap;
  }
  return scaleCharCapByBlockDimensions(baseCap, block, 8);
}

/** Décision : base 25 (texte total tronqué puis coupé en 2 lignes). */
export function getDecisionVisibleCharCap(
  fontSize?: number,
  block?: BlockDimensionInput
): number {
  const baseCap = getVisibleCharCap(fontSize, DECISION_DISPLAY_BASE_CHARS);
  if (!block || block.type !== 'decision') {
    return baseCap;
  }
  return scaleCharCapByBlockDimensions(baseCap, block, 8);
}

/** Au-dessous de ce seuil après troncature, une seule ligne dans le losange. */
export function getDecisionSingleLineThreshold(totalCap: number): number {
  return Math.min(18, Math.max(6, Math.floor(totalCap / 2)));
}

/**
 * Texte décision : troncature puis 1 ou 2 lignes (même logique partout : rendu, édition, DOM).
 * @param maxLength — capacité totale (ex. getDecisionVisibleCharCap(fontSize))
 */
export function splitDecisionTextIntoTwoLines(text: string, maxLength: number): string[] {
  if (!text) {
    return [text];
  }

  const truncatedText = text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  const singleLineMax = getDecisionSingleLineThreshold(maxLength);

  if (truncatedText.length <= singleLineMax) {
    return [truncatedText];
  }

  const midPoint = Math.floor(truncatedText.length / 2);
  let splitIndex = midPoint;

  for (let i = 0; i <= 5; i++) {
    if (truncatedText[midPoint + i] === ' ') {
      splitIndex = midPoint + i;
      break;
    }
    if (truncatedText[midPoint - i] === ' ') {
      splitIndex = midPoint - i;
      break;
    }
  }

  const line1 = truncatedText.slice(0, splitIndex).trim();
  const line2 = truncatedText.slice(splitIndex).trim();

  return [line1, line2];
}

/** Positionne un post-it juste sous une tâche (aligné, gap 16px). */
export function getNotePositionNearTask(task: Block): { x: number; y: number } {
  const NOTE_W = STYLES.note.width;
  const GAP = 16;
  const { width: tw, height: th } = getBlockDimensions(task);
  const tx = task.x || 0;
  const ty = task.y || 0;
  return {
    x: tx + Math.max(0, Math.floor((tw - NOTE_W) / 2)),
    y: ty + th + GAP,
  };
}

export function getBlockDimensions(block: Block): { width: number; height: number } {
  // Si le bloc a des dimensions personnalisées, les utiliser
  if (block.width && block.height) {
    return {
      width: block.width,
      height: block.height
    };
  }

  // Sinon, utiliser les dimensions par défaut selon le type
  switch (block.type) {
    case 'start':
    case 'end':
      return {
        width: STYLES.startEnd.width,
        height: STYLES.startEnd.height
      };
    case 'process':
      return {
        width: STYLES.process.width,
        height: STYLES.process.height
      };
    case 'decision':
      return {
        width: STYLES.decision.width,
        height: STYLES.decision.height
      };
    case 'note':
      return {
        width: STYLES.note.width,
        height: STYLES.note.height
      };
    default:
      return {
        width: STYLES.process.width,
        height: STYLES.process.height
      };
  }
}

export function getBlockFill(block: Block): string {
  return block.fill || STYLES.colors.getBlockColors(block.type).bgColor;
}

export function removeBlockAndConnections(
  blocks: Block[],
  blockId: string,
  lines?: Line[]
): { updatedBlocks: Block[]; updatedLines: Line[] | undefined } {
  return removeBlocksAndConnections(blocks, new Set([blockId]), lines);
}

export function removeBlocksAndConnections(
  blocks: Block[],
  deletedIds: Set<string>,
  lines?: Line[]
): { updatedBlocks: Block[]; updatedLines: Line[] | undefined } {
  const updatedBlocks = blocks.filter((b) => !deletedIds.has(b.id));
  const updatedLines = lines?.filter((line) => {
    const source = line.sourceId || line.from;
    const target = line.targetId || line.to;
    return !deletedIds.has(source || '') && !deletedIds.has(target || '');
  });

  return { updatedBlocks, updatedLines };
}

export function ensureBlockDimensions(block: Block): Block {
  const dimensions = getBlockDimensions(block);
  return {
    ...block,
    width: block.width || dimensions.width,
    height: block.height || dimensions.height
  };
}